import * as React from 'react';

import type { Recommender } from '@coursera/event-pulse-types';

import type { CarouselCollection, CollectionEntity } from 'bundles/collections-carousel/types/CarouselCollection';
import { getCollectionType } from 'bundles/collections-common/utils/collectionsUtils';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import LearningProductCard from 'bundles/product-card/components/LearningProductCard';
import LearningProductCardImage from 'bundles/product-card/components/LearningProductCardImage';
import {
  getImagePropOverrides,
  getPartnerNamesAndLogos,
} from 'bundles/product-card/components/utils/productCardV2Utils';

const PRODUCT_CARD_IMAGE_HEIGHT = 105;

type Props = {
  entity: CollectionEntity;
  entityIndex: number;
  collectionIndex: number;
  collection: CarouselCollection;
};

const CompactLearningProductCard = ({ entity, entityIndex, collectionIndex, collection }: Props) => {
  const getProductCardImageProps = () => {
    return { height: PRODUCT_CARD_IMAGE_HEIGHT };
  };
  return (
    <LearningProductCard
      ImageComponent={
        <LearningProductCardImage
          imageUrl={entity.imageUrl}
          imageParams={{
            ...getProductCardImageProps,
            ...getImagePropOverrides(entity.partners ?? [], entity.imageUrl ?? ''),
          }}
        />
      }
      name={entity.name || ''}
      titleComponent="h3"
      linkProps={{
        href: entity?.link,
        target: entity.forceOpenInNewTab ? '_blank' : undefined,
        rel: entity.forceOpenInNewTab ? 'noopener noreferrer' : undefined,
        component: TrackedLink2,
      }}
      partners={
        entity?.partners
          ? getPartnerNamesAndLogos(entity.partners, !!entity?.coBrandingEnabled, entity.partnerLogos)
          : []
      }
      avgProductRating={entity?.avgProductRating}
      productType={entity?.productType}
      variant="grid"
      id={entity.id}
      slug={entity?.slug}
      cardIndex={entityIndex}
      trackingProps={{
        trackedLinkProps: {
          trackingName: 'collection_product_card',
          withVisibilityTracking: true,
          requireFullyVisible: true,
          data: {
            itemIndex: entityIndex,
            collectionId: collection?.collectionTrackingData?.collectionId,
            ...collection.collectionTrackingData,
            ...(entity.trackingData || { id: entity.id, item: entity }),
            collectionIndex:
              collectionIndex !== undefined ? collectionIndex : collection?.collectionTrackingData?.collectionIndex,
          },
        },
        additionalV3TrackingData: {
          carousel: {
            id: collection?.collectionTrackingData?.id ?? collection?.collectionId ?? '',
            section:
              collectionIndex !== undefined ? collectionIndex : collection?.collectionTrackingData?.collectionIndex,
            model: collection?.collectionId as string,
            type: getCollectionType(collection?.collectionId as string),
            recommender: collection?.recommender as Recommender | undefined,
            name: collection?.title,
          },
        },
      }}
      recommender={collection?.recommender}
    />
  );
};

export default CompactLearningProductCard;
